import {
  roundCoordinate,
  type MapPickerHandle,
  type MapPickerOptions,
  type MapProvider,
} from './mapProvider';
import { loadGoogleMaps } from './googleMaps';

/**
 * Google Maps, behind the same seam OpenStreetMap sits behind.
 *
 * The component above never learns which of the two is drawing. That was the
 * point of the interface, and it is why this is one file: nothing outside it
 * imports `google`.
 *
 * THE PIN IS THE CENTRE OF THE MAP, not a draggable marker — the same
 * decision as the OSM provider, for the same reason. Dragging a marker on a
 * touch screen fights the gesture that pans the map, and the customer ends up
 * moving the map when they meant the pin. Moving the map under a fixed
 * crosshair is one gesture with one meaning, and it is what every ride-hailing
 * app does, so it needs no explaining.
 */

/** Google's own answer to the scroll-trap: ctrl/⌘ + wheel, two fingers on touch. */
const GESTURE_HANDLING = 'cooperative';

export const googleMapProvider: MapProvider = {
  name: 'google',

  async create(container: HTMLElement, options: MapPickerOptions): Promise<MapPickerHandle> {
    const google = await loadGoogleMaps();

    const map = new google.maps.Map(container, {
      center: options.center,
      zoom: options.zoom,
      // Everything that is not "which building" is noise in a checkout.
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      rotateControl: false,
      // A map in the middle of a form must not eat the page scroll. Google
      // solves this itself, so unlike the OSM provider there is no
      // enable-on-focus dance to maintain here.
      gestureHandling: GESTURE_HANDLING,
      clickableIcons: false,
    });

    // The current zoom, on the container — the same contract the OSM provider
    // publishes, so anything watching the map reads one attribute whichever
    // vendor is in use.
    const reportZoom = () => {
      container.dataset['zoom'] = String(map.getZoom() ?? options.zoom);
    };

    // See the OSM provider for the full reasoning: `setCenter` fires the same
    // event a customer's drag does, so without this flag a programmatic
    // recentre — a device fix, a search result — would report itself as the
    // customer placing a pin, and checkout would accept a point they never
    // chose.
    let programmatic = false;

    /**
     * Whether the map has finished arriving.
     *
     * GOOGLE FIRES `idle` ONCE WHEN THE MAP FIRST RENDERS, and Leaflet does
     * not — it fires `moveend` only for a move that happened. Reporting that
     * first one would make the two providers disagree about the single thing
     * this seam exists to hide: whether the customer has chosen a pin.
     *
     * The cost of getting it wrong is not cosmetic. The address step opens
     * its typed fields once a location exists, and the picker asks the device
     * where it is ONLY while nothing is set. So an initial report would hand
     * the customer a location they never chose — the shop's default centre —
     * open the form behind it, and skip the device prompt entirely, because
     * by then something IS set. The order would then be delivered to the
     * middle of the default map view.
     */
    let arrived = false;

    const report = () => {
      reportZoom();
      // Both flags are consumed here, not in branches: a `setCenter` during
      // the first render makes the opening `idle` both the arrival AND the
      // programmatic move, and leaving either flag standing would swallow
      // the customer's next real drag.
      const isArrival = !arrived;
      const wasProgrammatic = programmatic;
      arrived = true;
      programmatic = false;
      if (isArrival || wasProgrammatic) return;

      const centre = map.getCenter();
      if (!centre) return;
      options.onCenterChanged({
        lat: roundCoordinate(centre.lat()),
        lng: roundCoordinate(centre.lng()),
      });
    };

    // `idle` rather than `center_changed`: the latter fires continuously
    // through a drag, which would re-price the basket on every frame.
    const listener = map.addListener('idle', report);
    reportZoom();

    return {
      setCenter(next, zoom) {
        programmatic = true;
        map.setCenter(next);
        if (zoom !== undefined) map.setZoom(zoom);
      },
      destroy() {
        listener.remove();
        // Google has no `map.remove()`. Dropping the listener and letting the
        // container go is the documented teardown; React removes the node.
        //
        // Nothing else is touched. A stale handle's teardown can land after a
        // newer map has taken the same container — see the note in
        // `mapProvider` — so anything this did to the node would be done to
        // somebody else's map.
      },
    };
  },
};
